import { createBlock } from '../blockUtils';
import { stripHtml } from '../caretUtils';
import { indentOf } from '../indentUtils';
import { matchLanguage } from '../../types/CodeBlock';
import { TICK_INDETERMINATE, tickToProps } from '../tickOverrides';
import { inlineMd } from './inlineMarkdown';

/** Line-start checkbox marker: `[ ]`, `[]`, `[x]`, `[-]`, or Notion `- [ ]` / `* [x]`. */
export const CHECKBOX_LINE_RE = /^(?:[-*]\s+)?\[( |x|-)?\]\s+(.*)$/i;

export const checkboxTickFromMarker = (marker) => {
  const m = (marker || '').toLowerCase();
  if (m === 'x') return true;
  if (m === '-') return TICK_INDETERMINATE;
  return false;
};

/** A markdown table row: "| cell | cell |" (optional leading "> " from quotes). */
export const PIPE_ROW_RE = /^(?:>\s*)?\|.+\|\s*$/;

/** Pipe-row lines → table block. Dash separator rows are markdown syntax — dropped. */
export const pipeLinesToTableBlock = (pipeLines) => {
  const matrix = (pipeLines || [])
    .map((l) =>
      String(l)
        .replace(/^\s*>\s*/, '')
        .replace(/^\|/, '')
        .replace(/\|\s*$/, '')
        .split('|')
        // Same inline markdown as paragraphs: **bold**, *italic*, `code`
        .map((cell) => inlineMd(cell.trim()))
    )
    .filter((cells) => !cells.every((c) => /^:?-{3,}:?$/.test(stripHtml(c))));
  if (matrix.length === 0) return null;
  const width = Math.max(...matrix.map((r) => r.length));
  const pad = (row) => [...row, ...new Array(Math.max(0, width - row.length)).fill('')];
  const headers = pad(matrix[0]);
  const rows = matrix.slice(1).map(pad);
  return createBlock('table', '', {
    tableData: { headers, rows: rows.length > 0 ? rows : [new Array(width).fill('')] },
  });
};

/**
 * Markdown-ish plain text → blocks (Notion's markdown export flavor):
 *   #…###### headings (5+ clamp to H4)      - / * bullets   1. numbered
 *   - [ ] / [] / [x] / [-] checkboxes        --- dividers
 *   > quote runs → ❝ callouts               <aside>…</aside> → callouts
 *   | pipe | tables | → table blocks        inline `code` / **bold** / *italic*
 *   ``` fenced code → code blocks           image/video placeholder lines dropped
 * Aside emoji icons are ported; Notion's built-in /icons/*.svg fall back to
 * our default.
 */
export const textToBlocks = (text) => {
  const lines = (text || '').replace(/\r\n?/g, '\n').split('\n');
  const rootBlocks = [];
  const calloutStack = []; // open <aside> callouts (innermost last)
  const target = () =>
    calloutStack.length ? calloutStack[calloutStack.length - 1].children : rootBlocks;

  // Markdown separates blocks with ONE blank line — that's syntax, not
  // content. Every EXTRA consecutive blank is a real empty paragraph the
  // author left on purpose, so N blanks → N−1 empty text blocks.
  let blankRun = 0;
  const flushBlanks = () => {
    for (let i = 1; i < blankRun; i += 1) target().push(createBlock('text'));
    blankRun = 0;
  };

  // Consecutive "| … |" lines collect into ONE table block.
  let tableLines = null;
  const flushTable = () => {
    if (!tableLines) return;
    const table = pipeLinesToTableBlock(tableLines);
    tableLines = null;
    if (table) target().push(table);
  };

  // Consecutive "> " lines collect into ONE quote callout. Inner body is
  // re-parsed as markdown so "| … |" tables (and other structure) work
  // inside the quote — not only plain paragraphs.
  let quoteLines = null;
  const flushQuote = () => {
    if (!quoteLines) return;
    const contentLines = quoteLines.filter((l) => l.trim());
    quoteLines = null;
    if (!contentLines.length) return;
    const quote = createBlock('callout', '', { icon: '❝', variant: 'quote' });
    const inner = textToBlocks(contentLines.join('\n'));
    quote.children = inner.length ? inner : [createBlock('text')];
    target().push(quote);
  };

  // ``` / ```lang fenced runs → ONE code block (body kept verbatim).
  let codeFence = null; // { lang, lines: string[] }
  const flushCode = () => {
    if (!codeFence) return;
    const content = codeFence.lines.join('\n');
    const lang = matchLanguage(codeFence.lang) || 'plaintext';
    codeFence = null;
    target().push(
      createBlock('code', content, { language: lang, wrapText: true, showLineNumbers: false })
    );
  };

  lines.forEach((rawLine) => {
    // Fenced code: open/close on a line that is only ``` (optional lang).
    // While open, every line is literal — no bullet/quote/table parsing.
    const fence = rawLine.match(/^(\s*)```([\w+-]*)\s*$/);
    if (fence) {
      if (codeFence) {
        flushCode();
      } else {
        flushQuote();
        flushTable();
        flushBlanks();
        codeFence = { lang: fence[2] || '', lines: [] };
      }
      return;
    }
    if (codeFence) {
      codeFence.lines.push(rawLine);
      return;
    }

    const line = rawLine.trim();
    // Leading whitespace = nesting (Notion markdown exports 4 spaces/level)
    const leading = rawLine.match(/^[\t ]*/)[0].replace(/\t/g, '    ').length;
    const indentLevel = Math.min(6, Math.floor(leading / 4));
    const indentProps = indentLevel ? { indent: indentLevel } : {};

    const quote = line.match(/^>\s?(.*)$/);
    if (quote) {
      (quoteLines = quoteLines || []).push(quote[1]);
      return;
    }
    flushQuote(); // any non-">" line (blank ones included) ends the quote

    if (/^\|.*\|/.test(line)) {
      (tableLines = tableLines || []).push(line);
      return;
    }
    flushTable(); // any non-"|" line (blank ones included) ends the table

    if (!line) {
      blankRun += 1;
      return;
    }
    flushBlanks();

    if (/^<aside>?$/i.test(line)) {
      // No default icon — Notion emoji alts (if any) are applied on the <img> line
      const callout = createBlock('callout');
      callout.children = [];
      target().push(callout);
      calloutStack.push(callout);
      return;
    }
    if (/^<\/aside>$/i.test(line)) {
      const closed = calloutStack.pop();
      if (closed && closed.children.length === 0) closed.children = [createBlock('text')];
      return;
    }
    // Notion callout icon line: <img src="/icons/…" …> — emoji alts are kept
    if (/^<img\b[^>]*>$/i.test(line)) {
      const open = calloutStack[calloutStack.length - 1];
      const alt = line.match(/alt="([^"]*)"/i)?.[1]?.trim();
      if (open && alt && !alt.startsWith('/') && !alt.includes('.') && alt.length <= 4) {
        open.props.icon = alt;
      }
      return;
    }
    const heading = line.match(/^(#{1,6})\s+(.*)$/);
    if (heading) {
      target().push(
        createBlock('heading', inlineMd(heading[2]), {
          level: Math.min(heading[1].length, 4),
        })
      );
      return;
    }
    // "- [ ] task" (Notion) or bare "[ ] task" — checkbox beats bullet
    const checkbox = line.match(CHECKBOX_LINE_RE);
    if (checkbox) {
      target().push(
        createBlock('checkbox', inlineMd(checkbox[2]), {
          ...tickToProps(checkboxTickFromMarker(checkbox[1])),
          ...indentProps,
        })
      );
      return;
    }
    if (/^-{3,}$/.test(line)) {
      target().push(createBlock('divider'));
      return;
    }
    const bullet = line.match(/^[-*]\s+(.*)$/);
    if (bullet) {
      target().push(createBlock('bullet', inlineMd(bullet[1]), indentProps));
      return;
    }
    const numbered = line.match(/^(\d+)\.\s+(.*)$/);
    if (numbered) {
      const n = parseInt(numbered[1], 10);
      const siblings = target();
      const prev = siblings[siblings.length - 1];
      const continues = prev?.type === 'number' && indentOf(prev) === indentLevel;
      const startProps = !continues && n > 1 ? { start: n } : {};
      target().push(
        createBlock('number', inlineMd(numbered[2]), { ...indentProps, ...startProps })
      );
      return;
    }
    // Image / video placeholder lines ("!image.png", "…(1).mp4.mp4)") — no
    // file travels on the clipboard, so these are just noise. Checked last
    // so structured lines (checkboxes, bullets…) mentioning files survive.
    if (/^!\S/.test(line) || /\.(png|jpe?g|gif|webp|mp4|mov)\)?$/i.test(line)) {
      return;
    }
    target().push(createBlock('text', inlineMd(line), indentProps));
  });

  flushQuote();
  flushTable();
  // Unclosed fence: still emit as a code block (better than leaking ``` lines)
  flushCode();
  // Unclosed <aside>: keep whatever was collected
  calloutStack.forEach((c) => {
    if (c.children.length === 0) c.children = [createBlock('text')];
  });
  return rootBlocks;
};
